/** Inspector (spec §15.7): label, IRI, types, literal properties, extra variables, incident edges. */
import { Copy, ExternalLink, Share2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { parseIri } from "../lib/iri";
import type { GraphEdge, GraphNode } from "./conventionMapper";
import type { NodeInfo } from "./nodeInfo";
import { useT } from "../i18n";

export function Inspector({ workspaceId, node, info, edges, onExpand }: { workspaceId: string; node: GraphNode | null; info: NodeInfo | undefined; edges: GraphEdge[]; onExpand(id: string): void }) {
  const t = useT();
  const navigate = useNavigate();
  if (!node) return <p className="p-3 text-sm text-neutral-500">{t("inspector.empty")}</p>;
  const parsed = parseIri(node.id);
  const openable = parsed?.doc && ["block", "doc", "annotation", "structure", "reaction", "participation", "condition", "quantity", "checklist_item"].includes(parsed.kind);
  const incident = edges.filter((e) => e.source === node.id || e.target === node.id);
  return (
    <div className="space-y-3 p-3 text-sm" data-testid="inspector">
      <div>
        <div className="font-semibold" data-testid="inspector-label">{info?.label ?? node.id}</div>
        <div className="flex items-center gap-1 break-all text-xs text-neutral-500">
          {node.id}
          <button title={t("inspector.copyIri")} onClick={() => void navigator.clipboard.writeText(node.id)}>
            <Copy size={12} />
          </button>
        </div>
        <div className="text-xs text-neutral-500">{info?.kind ?? node.kind} · {t("inspector.style", { style: info?.style ?? "" })}</div>
      </div>
      <div className="flex gap-2">
        {openable && (
          <button
            className="flex items-center gap-1 rounded border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
            data-testid="inspector-open"
            onClick={() =>
              navigate({ to: "/w/$workspaceId/d/$docId", params: { workspaceId, docId: parsed.doc! }, hash: parsed.treeId ? `block=${parsed.treeId}` : undefined })
            }
          >
            <ExternalLink size={12} /> {t("inspector.open")}
          </button>
        )}
        <button className="flex items-center gap-1 rounded border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700" data-testid="inspector-expand" onClick={() => onExpand(node.id)}>
          <Share2 size={12} /> {t("inspector.expand")}
        </button>
      </div>
      {(info?.types.length ?? 0) > 0 && (
        <Section title={t("inspector.types")}>
          {info!.types.map((type) => (
            <div key={type} className="break-all text-xs">{type}</div>
          ))}
        </Section>
      )}
      {Object.keys(node.props).length > 0 && (
        <Section title={t("inspector.properties")}>
          {Object.entries(node.props).map(([p, vs]) => (
            <div key={p} className="text-xs">
              <span className="text-neutral-500">{p.split(/[#/]/).pop()}</span>: {vs.join(", ")}
            </div>
          ))}
        </Section>
      )}
      {Object.keys(node.extras).length > 0 && (
        <Section title={t("inspector.variables")}>
          {Object.entries(node.extras).map(([v, vs]) => (
            <div key={v} className="text-xs">?{v} = {vs.join(", ")}</div>
          ))}
        </Section>
      )}
      <Section title={t("inspector.edges", { count: incident.length })}>
        {incident.map((e) => (
          <div key={e.id} className="text-xs" data-testid="inspector-edge">
            {e.source === node.id ? "→" : "←"} {e.predicate.split(/[#/]/).pop()} <span className="text-neutral-500">[{e.provenance}{e.conf !== undefined ? ` ${e.conf.toFixed(2)}` : ""}{e.stale ? `, ${t("inspector.stale")}` : ""}]</span>
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase text-neutral-500">{title}</div>
      {children}
    </div>
  );
}
